import React from 'react';
import {
  ActionIcon,
  Box,
  Button,
  Card,
  Checkbox,
  CloseButton,
  Group,
  NumberInput,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconRefresh, IconTrash } from '@tabler/icons-react';
import type { CalendarDayRecord } from '../../../api/schedulingApis';
import { PermissionRestrictedTooltip } from '../../../permissions/scheduler';

interface DayDraft {
  calendarDate: string;
  isWorking: boolean;
  workingMinutes: number;
  reason: string;
}

interface CalendarOverridesTabProps {
  calendarSelected: boolean;
  tableViewportHeight: string;
  daySearch: string;
  setDaySearch: React.Dispatch<React.SetStateAction<string>>;
  filteredDays: CalendarDayRecord[];
  dayDraft: DayDraft;
  setDayDraft: React.Dispatch<React.SetStateAction<DayDraft>>;
  parseDateYmd: (value: string) => Date | null;
  formatDateYmd: (value: Date | string | null) => string;
  onAddOverride: () => Promise<void>;
  onSyncOverride: (row: CalendarDayRecord) => Promise<void>;
  onDeleteOverride: (id: string) => Promise<void>;
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

export default function CalendarOverridesTab(props: CalendarOverridesTabProps) {
  const {
    calendarSelected,
    tableViewportHeight,
    daySearch,
    setDaySearch,
    filteredDays,
    dayDraft,
    setDayDraft,
    parseDateYmd,
    formatDateYmd,
    onAddOverride,
    onSyncOverride,
    onDeleteOverride,
    canCreate = true,
    canEdit = true,
    canDelete = true,
  } = props;

  return (
    <Card withBorder radius="md" p="md" style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack style={{ flex: 1, minHeight: 0 }}>
        <Group align="stretch" wrap="nowrap" gap="md">
          <Box style={{ flex: '1 1 64%', minWidth: 0 }}>
            <Group justify="space-between" align="center" h="100%" wrap="nowrap">
              <Title order={4}>Specific day overrides</Title>
              <TextInput
                placeholder="Search by date or reason..."
                value={daySearch}
                onChange={(e) => setDaySearch(e.currentTarget.value)}
                size="xs"
                rightSection={
                  daySearch ? (
                    <CloseButton aria-label="Clear search" onClick={() => setDaySearch('')} />
                  ) : null
                }
                w={{ base: '100%', sm: 260 }}
              />
            </Group>
          </Box>
          <Box style={{ flex: '0 1 32%', minWidth: 280 }}>
            <Group justify="flex-start" align="center" h="100%">
              <Text size="sm" fw={600}>Add new override</Text>
            </Group>
          </Box>
        </Group>
        <Group align="stretch" wrap="nowrap" gap="md" style={{ flex: 1, minHeight: 0 }}>
          <Box style={{ flex: '1 1 64%', minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Stack gap="sm" style={{ flex: 1, minHeight: 0 }}>
              <ScrollArea h={tableViewportHeight} type="auto" offsetScrollbars>
                <Table striped highlightOnHover withTableBorder>
                  <Table.Thead
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      background: 'var(--mantine-color-body)',
                    }}
                  >
                    <Table.Tr>
                      <Table.Th>Date</Table.Th><Table.Th>Working</Table.Th><Table.Th>Minutes</Table.Th><Table.Th>Reason</Table.Th><Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {filteredDays.length > 0 ? (
                      filteredDays.map((d) => (
                        <Table.Tr key={d.id}>
                          <Table.Td>{d.calendarDate}</Table.Td>
                          <Table.Td>{d.isWorking ? 'Yes' : 'No'}</Table.Td>
                          <Table.Td>{d.workingMinutes}</Table.Td>
                          <Table.Td>{d.reason || '-'}</Table.Td>
                          <Table.Td>
                            <Group gap={6} justify="flex-end">
                              <PermissionRestrictedTooltip restricted={!canEdit}>
                                <ActionIcon
                                  variant="light"
                                  disabled={!canEdit}
                                  onClick={() => { void onSyncOverride(d); }}
                                >
                                  <IconRefresh size={14} />
                                </ActionIcon>
                              </PermissionRestrictedTooltip>
                              <PermissionRestrictedTooltip restricted={!canDelete}>
                                <ActionIcon
                                  color="red"
                                  variant="light"
                                  disabled={!canDelete}
                                  onClick={() => { void onDeleteOverride(d.id); }}
                                >
                                  <IconTrash size={14} />
                                </ActionIcon>
                              </PermissionRestrictedTooltip>
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      ))
                    ) : (
                      <Table.Tr>
                        <Table.Td colSpan={5}>
                          <Text size="sm" c="dimmed" ta="center" py="lg" fw={600}>
                            {calendarSelected ? 'No overrides to show' : 'Calendar not selected'}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Stack>
          </Box>
          <Card withBorder radius="md" p="sm" style={{ flex: '0 1 32%', minWidth: 280, height: '100%', minHeight: 0, overflow: 'auto' }}>
            <Stack>
              <Checkbox
                label="Working day"
                checked={dayDraft.isWorking}
                onChange={(e) => setDayDraft((s) => ({ ...s, isWorking: e.currentTarget.checked }))}
              />
              <DateInput
                label="Date"
                value={parseDateYmd(dayDraft.calendarDate)}
                onChange={(v) => setDayDraft((s) => ({ ...s, calendarDate: formatDateYmd(v) }))}
              />
              <NumberInput
                label="Working minutes"
                value={dayDraft.workingMinutes}
                onChange={(v) => setDayDraft((s) => ({ ...s, workingMinutes: Number(v || 0) }))}
                min={0}
              />
              <TextInput
                label="Reason"
                value={dayDraft.reason}
                onChange={(e) => setDayDraft((s) => ({ ...s, reason: e.currentTarget.value }))}
              />
              <PermissionRestrictedTooltip restricted={!canCreate} fullWidth>
                <Button
                  disabled={!calendarSelected || !dayDraft.calendarDate || !canCreate}
                  onClick={() => { void onAddOverride(); }}
                  fullWidth
                >
                  Add day override
                </Button>
              </PermissionRestrictedTooltip>
            </Stack>
          </Card>
        </Group>
      </Stack>
    </Card>
  );
}
